<template>
  <section class="loan-details__hero">
    <canvas v-show="$device.isMedium"
            ref="canvas"
            v-bind="heroBgAttrs"
            class="loan-details__hero-bg" />
    <div class="loan-details__container grid-container" :class="{ full: !$device.isMedium }">
      <div class="grid-x">
        <div class="gallery-cell cell small-12 medium-7">
          <Gallery id="loanGallery" :main="loan.mainImage" :secondary="loan.secondaryImages" />
        </div>
        <div class="cell small-12 medium-4 medium-offset-1">
          <div id="loanSideInfo">
            <div class="grid-y position-relative">
              <div class="cell indicator-cell">
                <FundingProgressIndicator class="indicator"
                                          :funded="fundedAmount"
                                          :requested="loan.fundingAmount"
                                          :size="indicatorSize"
                                          :status="loan.status" />
              </div>
              <div class="cell description-cell">
                <div class="grid-x">
                  <div class="cell small-9 medium-12">
                    <div class="description" v-html="description" />
                  </div>
                </div>
              </div>
              <div class="cell microloan-form-cell">
                <MicroloanForm v-if="isLendingAllowed"
                               id="microloanForm"
                               :currency="loan.fundingCurrency"
                               :loan-id="loan.id"
                               :remaining="loan.fundingAmountRemaining"
                               :entrepreneur-display-name="loan.entrepreneur.displayName" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
  import stackblur from 'stackblur-canvas'
  import { mapGetters } from 'vuex'
  import FundingProgressIndicator
    from '~/modules/loans/components/frontend/funding-progress-indicator'
  import Gallery from '~/modules/loans/components/frontend/loan-details-gallery'
  import MicroloanForm from '~/modules/loans/components/frontend/microloan-form'
  import LoanStatusMixin from '~/modules/loans/mixins/loan-status'
  import { default as clFactory } from '~/utils/cloudinary'
  import i18n from './locales'

  const cl = clFactory()

  export default {
    name: 'LoanDetailsHero',
    i18n,
    mixins: [
      LoanStatusMixin,
    ],
    components: {
      FundingProgressIndicator,
      Gallery,
      MicroloanForm,
    },
    data () {
      return {
        heroBg: new Image(),
        heroBgAttrs: {
          id: 'heroCanvas',
          height: 600,
          width: 800,
        },
      }
    },
    computed: {
      ...mapGetters({
        loan: 'loans/current',
      }),
      description () {
        const type = this.loan.entrepreneur.type
        const gender = type === 'individual' ? this.loan.entrepreneur.gender : null
        const name = type === 'individual'
          ? this.loan.entrepreneur.firstName
          : this.loan.entrepreneur.groupName
        const amount = this.status === 'open'
          ? this.loan.formattedFundingAmountRemaning
          : this.loan.formattedFundingAmount

        let localePath = `descriptions.${this.status}.${type}`
        if (type === 'individual') {
          localePath += `.${gender}`
        }

        const translated = this.$t(localePath, { name, amount })

        return translated.replace(`s's`, `s'`)
      },
      fundedAmount () {
        const requested = this.$get(this.loan, 'fundingAmount', 0)
        const remaining = this.$get(this.loan, 'fundingAmountRemaining', 0)

        return requested - remaining
      },
      indicatorSize () {
        return this.$device.isMedium ? 'large' : 'small'
      },
      isLendingAllowed () {
        return this.$get(this.loan, 'status') === 'approved'
      },
      loanImages () {
        return [
          cl.url(this.loan.mainImage.publicId),
          ...this.loan.images
            .filter(image => !image.isMain)
            .map(image => cl.url(image.publicId)),
        ]
      },
      backgroundImage () {
        const { height, width } = this.heroBgAttrs

        if(this.loan.status === 'defaulted' || this.loan.status === 'devalued') {
          return '/img/user-profile-bg.jpg'
        }

        return cl.url(this.loan.mainImage.fullPublicId, {
          crop: 'fit',
          height,
          width,
        })
      },
    },
    methods: {
      renderBackgroundImage () {
        const vm = this
        const canvas = this.$refs.canvas
        const context = canvas.getContext('2d')
        const { height, width } = this.heroBgAttrs

        this.heroBg.crossOrigin = 'Anonymous'
        this.heroBg.onload = function () {
          vm.$nextTick(() => {
            context.drawImage(this, 0, 0, width, height)
            stackblur.canvasRGB(canvas, 0, 0, width, height, 18)
          })
        }
        this.heroBg.src = this.backgroundImage
      },
    },
    mounted () {
      this.renderBackgroundImage()
    },
  }
</script>

<style lang="scss" scoped>
  @import "~assets/scss/frontend/_colours";
  @import "~assets/scss/frontend/_settings";
  @import "~assets/scss/frontend/foundation/_settings";
  @import "~foundation-sites/scss/util/_breakpoint";
  @import "~foundation-sites/scss/util/_unit";
  @import "~ceaser-easing/ceaser-easings";

  .loan-details__hero {
    position: relative;
    overflow: hidden;
    z-index: 0;

    .loan-details__hero-bg {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      z-index: -1;
    }

    .loan-details__container {
      @each $breakpoint, $padding in $loan-page-container-vertical-padding {
        @include breakpoint($breakpoint) {
          padding: {
            bottom: $padding;
            top: $padding;
          }
        }
      }

      .gallery-cell {
        #loanGallery {
          height: 100%;
          width: 100%;
        }
      }

      #loanSideInfo {
        $border: $global-border;
        $loanSideInfoPadding: rem-calc(map-get($grid-column-gutter, small) / 2);

        background-color: $colour-magnolia;

        & > .grid-y > .cell {
          padding: {
            left: $loanSideInfoPadding;
            right: $loanSideInfoPadding;
          }
        }

        .indicator-cell {
          position: absolute;
          right: 1rem;
          transform: translateY(-50%);

          @include breakpoint (medium) {
            align-items: center;
            display: flex;
            justify-content: center;
            padding: rem-calc(20);
            position: relative;
            right: auto;
            transform: none;
          }
        }

        .description-cell {
          background-color: $colour-magnolia-light;
          border: {
            bottom: $border;
            top: $border;
          }
          padding: {
            bottom: $loanSideInfoPadding * 2;
            top: $loanSideInfoPadding * 2;
            right: rem-calc(80) + 2rem;
          }

          @include breakpoint (medium) {
            border: $global-border;
            padding: $loanSideInfoPadding * 2;
            text-align: center;
          }
        }

        .microloan-form-cell {
          padding: {
            bottom: $loanSideInfoPadding * 2;
            top: $loanSideInfoPadding * 2;
          }
        }
      }
    }
  }
</style>
